import { useEffect, useState, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays, addDays } from 'date-fns';
import api from '../api/client';

/* ────────────────────────────────────────────
   Helpers ISO 8601 (Lunes a Domingo, semana del 4 de enero)
   ──────────────────────────────────────────── */
function isoDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function getLunesYDomingoDeSemana(year, semana) {
  const ene4 = new Date(Date.UTC(year, 0, 4));
  const dow = ene4.getUTCDay() || 7;
  const lunesSem1 = new Date(ene4);
  lunesSem1.setUTCDate(ene4.getUTCDate() - dow + 1);

  const lunes = new Date(lunesSem1);
  lunes.setUTCDate(lunesSem1.getUTCDate() + (semana - 1) * 7);

  const domingo = new Date(lunes);
  domingo.setUTCDate(lunes.getUTCDate() + 6);

  return { lunes: isoDate(lunes), domingo: isoDate(domingo) };
}

const fmtCorto = (iso) => {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
};

function agruparPorHora(puntos) {
  const buckets = {};
  for (let h = 0; h < 24; h++) buckets[h] = { oxigeno: [], temperatura: [] };
  for (const p of puntos) {
    const h = new Date(p.fecha_hora).getHours();
    if (p.oxigeno_mg_l != null) buckets[h].oxigeno.push(Number(p.oxigeno_mg_l));
    if (p.temperatura_c != null) buckets[h].temperatura.push(Number(p.temperatura_c));
  }
  const out = [];
  for (let h = 0; h < 24; h++) {
    const ox = buckets[h].oxigeno;
    const tp = buckets[h].temperatura;
    out.push({
      hora: h,
      label: `${String(h).padStart(2, '0')}:00`,
      oxigeno: ox.length ? ox.reduce((a, b) => a + b, 0) / ox.length : null,
      temperatura: tp.length ? tp.reduce((a, b) => a + b, 0) / tp.length : null,
    });
  }
  return out;
}

function serieSecuencial(puntos, etiqueta) {
  return puntos.map((p, i) => {
    const fecha = new Date(p.fecha_hora);
    return {
      idx: i,
      label: format(fecha, 'dd/MM HH:mm'),
      [`oxigeno_${etiqueta}`]: p.oxigeno_mg_l != null ? Number(p.oxigeno_mg_l) : null,
      [`temperatura_${etiqueta}`]: p.temperatura_c != null ? Number(p.temperatura_c) : null,
    };
  });
}

function calcularKpis(puntos, tipo) {
  const campo = tipo === 'oxigeno' ? 'oxigeno_mg_l' : 'temperatura_c';
  const vals = puntos.map((p) => Number(p[campo])).filter((v) => !isNaN(v));
  if (!vals.length) return { n: 0, avg: null, min: null, max: null };
  return {
    n: vals.length,
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
    min: Math.min(...vals),
    max: Math.max(...vals),
  };
}

export default function CompararParametros({ piscinas = [], piscinaSeleccionada = '' }) {
  const [tipo, setTipo] = useState('dia');
  const [fechaA, setFechaA] = useState('');
  const [fechaB, setFechaB] = useState('');
  const [desdeA, setDesdeA] = useState('');
  const [hastaA, setHastaA] = useState('');
  const [desdeB, setDesdeB] = useState('');
  const [hastaB, setHastaB] = useState('');
  const [semanaA, setSemanaA] = useState('');
  const [semanaB, setSemanaB] = useState('');
  const [metrica, setMetrica] = useState('oxigeno');
  const [modo, setModo] = useState('hora');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [semanasDisponibles, setSemanasDisponibles] = useState([]);
  const [cargandoSemanas, setCargandoSemanas] = useState(false);

  useEffect(() => {
    setDatos(null);
    setSemanaA('');
    setSemanaB('');
  }, [piscinaSeleccionada]);

  useEffect(() => {
    if (!piscinaSeleccionada) {
      setSemanasDisponibles([]);
      setSemanaA('');
      setSemanaB('');
      return;
    }
    let activo = true;
    setCargandoSemanas(true);
    api.get('/parametros/semanas-con-datos', { params: { piscina_id: piscinaSeleccionada } })
      .then(({ data }) => {
        if (!activo) return;
        const conRango = (data || []).map((s) => {
          const { lunes, domingo } = getLunesYDomingoDeSemana(s.year, s.semana);
          return { ...s, lunes, domingo };
        });
        setSemanasDisponibles(conRango);
        if (conRango.length > 0 && tipo === 'semana') {
          const key = `${conRango[0].year}-${String(conRango[0].semana).padStart(2, '0')}`;
          if (!semanaA) setSemanaA(key);
          if (!semanaB) setSemanaB(key);
        }
      })
      .catch(() => {
        if (activo) setSemanasDisponibles([]);
      })
      .finally(() => {
        if (activo) setCargandoSemanas(false);
      });
    return () => { activo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [piscinaSeleccionada]);

  const cambiarTipo = (nuevo) => {
    setTipo(nuevo);
    setDatos(null);
    setError(null);
    if (nuevo === 'semana' && semanasDisponibles.length > 0) {
      const primera = semanasDisponibles[0];
      const key = `${primera.year}-${String(primera.semana).padStart(2, '0')}`;
      if (!semanaA) setSemanaA(key);
      if (!semanaB) setSemanaB(key);
    }
  };

  const calcularRango = () => {
    if (tipo === 'dia') return { A: { d: fechaA, h: fechaA }, B: { d: fechaB, h: fechaB } };
    if (tipo === 'rango') return { A: { d: desdeA, h: hastaA }, B: { d: desdeB, h: hastaB } };
    const semA = semanasDisponibles.find((s) => `${s.year}-${String(s.semana).padStart(2, '0')}` === semanaA);
    const semB = semanasDisponibles.find((s) => `${s.year}-${String(s.semana).padStart(2, '0')}` === semanaB);
    return {
      A: semA ? { d: semA.lunes, h: semA.domingo } : { d: '', h: '' },
      B: semB ? { d: semB.lunes, h: semB.domingo } : { d: '', h: '' },
    };
  };

  const comparar = async () => {
    const { A, B } = calcularRango();
    if (!piscinaSeleccionada || !A.d || !A.h || !B.d || !B.h) {
      alert('Selecciona una piscina en el filtro de arriba y completa las fechas de ambos lados');
      return;
    }
    setCargando(true);
    setError(null);
    try {
      // ✅ FIX DE ZONA HORARIA PARA AMBOS RANGOS (A y B)
      const desdeAAjustado = format(subDays(new Date(`${A.d}T12:00:00`), 1), "yyyy-MM-dd'T'00:00:00");
      const hastaAAjustado = format(addDays(new Date(`${A.h}T12:00:00`), 1), "yyyy-MM-dd'T'23:59:59");
      
      const desdeBAjustado = format(subDays(new Date(`${B.d}T12:00:00`), 1), "yyyy-MM-dd'T'00:00:00");
      const hastaBAjustado = format(addDays(new Date(`${B.h}T12:00:00`), 1), "yyyy-MM-dd'T'23:59:59");

      const { data } = await api.get('/parametros/comparar', {
        params: {
          piscina_id: piscinaSeleccionada,
          desdeA: desdeAAjustado, hastaA: hastaAAjustado,
          desdeB: desdeBAjustado, hastaB: hastaBAjustado,
        },
      });

      // Filtramos los puntos recibidos en el frontend para dejar SOLO los del día exacto
      const filtrarPorFechaLocal = (puntos, fechaInicio, fechaFin) => {
        return puntos.filter(p => {
          const f = p.fecha_hora.substring(0, 10);
          return f >= fechaInicio && f <= fechaFin;
        });
      };

      const rangoAFiltrado = filtrarPorFechaLocal(data.rangoA.puntos, A.d, A.h);
      const rangoBFiltrado = filtrarPorFechaLocal(data.rangoB.puntos, B.d, B.h);

      setDatos({
        ...data,
        rangoA: { ...data.rangoA, puntos: rangoAFiltrado, desde: A.d, hasta: A.h },
        rangoB: { ...data.rangoB, puntos: rangoBFiltrado, desde: B.d, hasta: B.h }
      });

    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  };

  let chartData = [];
  if (datos) {
    if (modo === 'hora') {
      const A = agruparPorHora(datos.rangoA.puntos);
      const B = agruparPorHora(datos.rangoB.puntos);
      chartData = A.map((row, i) => ({
        label: row.label,
        oxigenoA: row.oxigeno,
        oxigenoB: B[i].oxigeno,
        temperaturaA: row.temperatura,
        temperaturaB: B[i].temperatura,
      }));
    } else {
      const A = serieSecuencial(datos.rangoA.puntos, 'A');
      const B = serieSecuencial(datos.rangoB.puntos, 'B');
      const maxLen = Math.max(A.length, B.length);
      chartData = Array.from({ length: maxLen }, (_, i) => ({
        label: A[i]?.label || B[i]?.label || `#${i + 1}`,
        oxigenoA: A[i]?.oxigeno_A ?? null,
        oxigenoB: B[i]?.oxigeno_B ?? null,
        temperaturaA: A[i]?.temperatura_A ?? null,
        temperaturaB: B[i]?.temperatura_B ?? null,
      }));
    }
  }

  const kpisA = datos ? calcularKpis(datos.rangoA.puntos, metrica === 'temperatura' ? 'temperatura' : 'oxigeno') : null;
  const kpisB = datos ? calcularKpis(datos.rangoB.puntos, metrica === 'temperatura' ? 'temperatura' : 'oxigeno') : null;
  const unidad = metrica === 'temperatura' ? '°C' : 'mg/L';

  const inputBase = 'w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent';
  const labelBase = 'text-[11px] font-medium text-gray-600';

  const semA = semanasDisponibles.find((s) => `${s.year}-${String(s.semana).padStart(2, '0')}` === semanaA);
  const semB = semanasDisponibles.find((s) => `${s.year}-${String(s.semana).padStart(2, '0')}` === semanaB);
  const aniosDisponibles = [...new Set(semanasDisponibles.map((s) => s.year))].sort((a, b) => b - a);

  const renderSelectSemana = (value, setValue, colorClass) => (
    <>
      {!piscinaSeleccionada ? (
        <div className="mt-1 px-3 py-2 border rounded-lg text-xs bg-gray-50 text-gray-500">
          Selecciona una piscina en el filtro de arriba primero
        </div>
      ) : cargandoSemanas ? (
        <div className="mt-1 px-3 py-2 border rounded-lg text-xs bg-gray-50 text-gray-500">
          Cargando semanas...
        </div>
      ) : semanasDisponibles.length === 0 ? (
        <div className="mt-1 px-3 py-2 border rounded-lg text-xs bg-amber-50 border-amber-200 text-amber-700">
          ⚠️ Esta piscina no tiene datos de parámetros registrados
        </div>
      ) : (
        <>
          <select
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className={`mt-1 ${inputBase}`}
          >
            <option value="">— Seleccionar semana —</option>
            {aniosDisponibles.map((anio) => {
              const semanasAnio = semanasDisponibles.filter((s) => s.year === anio);
              return (
                <optgroup key={anio} label={`Año ${anio}`}>
                  {semanasAnio.map((s) => {
                    const key = `${s.year}-${String(s.semana).padStart(2, '0')}`;
                    return (
                      <option key={key} value={key}>
                        Semana {String(s.semana).padStart(2, '0')}: {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)} · {s.total} muestras
                      </option>
                    );
                  })}
                </optgroup>
              );
            })}
          </select>
          {value && (
            <div className={`mt-2 bg-white border rounded px-2 py-1 text-[11px] ${colorClass}`}>
              {(() => {
                const s = value === semanaA ? semA : semB;
                if (!s) return null;
                return (
                  <>
                    📊 <b>Semana {String(s.semana).padStart(2, '0')} · {s.year}</b>:{' '}
                    {fmtCorto(s.lunes)} → {fmtCorto(s.domingo)} · <b>{s.total}</b> muestras
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}
    </>
  );

  // Calcular los límites del eje Y dinámicamente para que la gráfica no se vea aplastada
  const yDomain = useMemo(() => {
    if (!chartData.length) return ['auto', 'auto'];
    const vals = [];
    chartData.forEach((d) => {
      if (d.oxigenoA != null) vals.push(d.oxigenoA);
      if (d.oxigenoB != null) vals.push(d.oxigenoB);
      if (d.temperaturaA != null) vals.push(d.temperaturaA);
      if (d.temperaturaB != null) vals.push(d.temperaturaB);
    });
    if (!vals.length) return ['auto', 'auto'];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const padding = (max - min) * 0.2 || 1; // 20% de margen o al menos 1 unidad
    return [Math.max(0, Math.floor(min - padding)), Math.ceil(max + padding)];
  }, [chartData]);

  return (
    <div className="space-y-3">
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="mb-3">
          <span className={`${labelBase} block mb-1`}>Tipo de comparación</span>
          <div className="flex gap-1.5 max-w-2xl">
            <button type="button" onClick={() => cambiarTipo('dia')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${tipo === 'dia' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              📅 Día vs Día
            </button>
            <button type="button" onClick={() => cambiarTipo('rango')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${tipo === 'rango' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              📆 Rango vs Rango
            </button>
            <button type="button" onClick={() => cambiarTipo('semana')}
              className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${tipo === 'semana' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              📊 Semana vs Semana
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3">
            <div className="text-[11px] font-bold text-blue-800 mb-2">🔵 Rango A</div>
            {tipo === 'dia' && (
              <label className="block">
                <span className={labelBase}>Fecha</span>
                <input type="date" value={fechaA} onChange={(e) => setFechaA(e.target.value)} className={`mt-1 ${inputBase}`} />
              </label>
            )}
            {tipo === 'rango' && (
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className={labelBase}>Desde</span>
                  <input type="date" value={desdeA} onChange={(e) => setDesdeA(e.target.value)} className={`mt-1 ${inputBase}`} />
                </label>
                <label className="block">
                  <span className={labelBase}>Hasta</span>
                  <input type="date" value={hastaA} onChange={(e) => setHastaA(e.target.value)} className={`mt-1 ${inputBase}`} />
                </label>
              </div>
            )}
            {tipo === 'semana' && (
              <label className="block">
                <span className={labelBase}>Semana ISO (Lunes a Domingo)</span>
                {renderSelectSemana(semanaA, setSemanaA, 'border-blue-200 text-blue-900')}
              </label>
            )}
          </div>

          <div className="bg-red-50/60 border border-red-200 rounded-lg p-3">
            <div className="text-[11px] font-bold text-red-800 mb-2">🔴 Rango B</div>
            {tipo === 'dia' && (
              <label className="block">
                <span className={labelBase}>Fecha</span>
                <input type="date" value={fechaB} onChange={(e) => setFechaB(e.target.value)} className={`mt-1 ${inputBase}`} />
              </label>
            )}
            {tipo === 'rango' && (
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className={labelBase}>Desde</span>
                  <input type="date" value={desdeB} onChange={(e) => setDesdeB(e.target.value)} className={`mt-1 ${inputBase}`} />
                </label>
                <label className="block">
                  <span className={labelBase}>Hasta</span>
                  <input type="date" value={hastaB} onChange={(e) => setHastaB(e.target.value)} className={`mt-1 ${inputBase}`} />
                </label>
              </div>
            )}
            {tipo === 'semana' && (
              <label className="block">
                <span className={labelBase}>Semana ISO (Lunes a Domingo)</span>
                {renderSelectSemana(semanaB, setSemanaB, 'border-red-200 text-red-900')}
              </label>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4">
            <span className={`${labelBase} block mb-1`}>Métrica</span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setMetrica('oxigeno')}
                className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition ${metrica === 'oxigeno' ? 'bg-cyan-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                💧 O₂
              </button>
              <button type="button" onClick={() => setMetrica('temperatura')}
                className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition ${metrica === 'temperatura' ? 'bg-orange-600 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                🌡️ T°
              </button>
              <button type="button" onClick={() => setMetrica('ambos')}
                className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition ${metrica === 'ambos' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                📊 Ambos
              </button>
            </div>
          </div>

          <div className="md:col-span-5">
            <span className={`${labelBase} block mb-1`}>Alineación</span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setModo('hora')}
                className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition ${modo === 'hora' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                🕐 Por hora del día
              </button>
              <button type="button" onClick={() => setModo('secuencial')}
                className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-medium transition ${modo === 'secuencial' ? 'bg-slate-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                📈 Por secuencia
              </button>
            </div>
          </div>

          <div className="md:col-span-3 flex items-end">
            <button onClick={comparar} disabled={cargando || !piscinaSeleccionada}
              className="w-full px-4 py-1.5 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 text-xs font-semibold disabled:opacity-50 shadow-sm">
              {cargando ? 'Cargando...' : '⚖️ Comparar'}
            </button>
          </div>
        </div>

        <div className="mt-3 text-[10px] text-gray-500 bg-blue-50/60 border border-blue-100 rounded-lg px-3 py-1.5 leading-relaxed">
          <b>💡</b>{' '}
          {tipo === 'dia' && 'Compara 2 días completos. Por hora del día es ideal para ver el patrón horario.'}
          {tipo === 'rango' && 'Compara 2 rangos de fechas. Por secuencia es mejor cuando hay distinta cantidad de muestras.'}
          {tipo === 'semana' && 'Solo aparecen las semanas con datos reales. Compara 2 semanas ISO (Lunes a Domingo).'}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-2.5 rounded-lg text-xs">{error}</div>
      )}

      {datos && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-blue-50 border border-blue-300 rounded-xl p-3">
            <div className="text-xs font-bold text-blue-900 mb-2">
              🔵 Rango A · {datos.rangoA.desde} → {datos.rangoA.hasta}
            </div>
            <div className="grid grid-cols-4 gap-1 text-center">
              <div>
                <div className="text-[10px] text-gray-500">Muestras</div>
                <div className="text-base font-bold text-blue-800">{kpisA.n}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500">Prom.</div>
                <div className="text-base font-bold text-blue-800">
                  {kpisA.avg != null ? kpisA.avg.toFixed(2) : '-'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500">Mín</div>
                <div className="text-base font-bold text-blue-800">
                  {kpisA.min != null ? kpisA.min.toFixed(2) : '-'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500">Máx</div>
                <div className="text-base font-bold text-blue-800">
                  {kpisA.max != null ? kpisA.max.toFixed(2) : '-'}
                </div>
              </div>
            </div>
            <div className="text-[10px] text-blue-700 mt-1 text-center">Unidad: {unidad}</div>
          </div>

          <div className="bg-red-50 border border-red-300 rounded-xl p-3">
            <div className="text-xs font-bold text-red-900 mb-2">
              🔴 Rango B · {datos.rangoB.desde} → {datos.rangoB.hasta}
            </div>
            <div className="grid grid-cols-4 gap-1 text-center">
              <div>
                <div className="text-[10px] text-gray-500">Muestras</div>
                <div className="text-base font-bold text-red-800">{kpisB.n}</div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500">Prom.</div>
                <div className="text-base font-bold text-red-800">
                  {kpisB.avg != null ? kpisB.avg.toFixed(2) : '-'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500">Mín</div>
                <div className="text-base font-bold text-red-800">
                  {kpisB.min != null ? kpisB.min.toFixed(2) : '-'}
                </div>
              </div>
              <div>
                <div className="text-[10px] text-gray-500">Máx</div>
                <div className="text-base font-bold text-red-800">
                  {kpisB.max != null ? kpisB.max.toFixed(2) : '-'}
                </div>
              </div>
            </div>
            <div className="text-[10px] text-red-700 mt-1 text-center">Unidad: {unidad}</div>
          </div>
        </div>
      )}

      {datos && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="text-sm font-semibold text-slate-700 mb-3">
            {datos.piscina?.piscina_codigo} · {datos.piscina?.sector_nombre}
          </div>
          {chartData.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">
              Sin datos en uno o ambos rangos.
            </div>
          ) : (
            <div style={{ width: '100%', height: 400 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} domain={yDomain} />
                  <Tooltip />
                  <Legend />
                  {(metrica === 'oxigeno' || metrica === 'ambos') && (
                    <>
                      <Line type="monotone" dataKey="oxigenoA" name="O₂ Rango A"
                        stroke="#00838F" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      <Line type="monotone" dataKey="oxigenoB" name="O₂ Rango B"
                        stroke="#00838F" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} connectNulls />
                    </>
                  )}
                  {(metrica === 'temperatura' || metrica === 'ambos') && (
                    <>
                      <Line type="monotone" dataKey="temperaturaA" name="T° Rango A"
                        stroke="#F97316" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      <Line type="monotone" dataKey="temperaturaB" name="T° Rango B"
                        stroke="#F97316" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} connectNulls />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}